import React, { useState } from 'react';
import { Popover, Box, List, ListItemButton, ListItemText } from '@mui/material';

export interface CargoContentFile {
  id: number;
  description: string;
}

interface RawCargoContent {
  contentId: number;
  content?: { description?: string };
}

interface CargoContentListProps {
  cargoContents: RawCargoContent[];
  onDownload: (file: CargoContentFile) => void;
  downloadTitle: string;
  expandTitle: string;
}

const MAX_VISIBLE_FILES = 2;
const FILE_NAME_MAX_WIDTH = 204;

const toFiles = (cargoContents: RawCargoContent[]): CargoContentFile[] =>
  (cargoContents ?? [])
    .filter((cargoContent) => !!cargoContent?.content?.description)
    .map((cargoContent) => ({
      id: cargoContent.contentId,
      description: cargoContent.content!.description as string,
    }));

const CargoContentList: React.FC<CargoContentListProps> = ({ cargoContents, onDownload, downloadTitle, expandTitle }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const files = toFiles(cargoContents);

  if (files.length === 0) {
    return <span>—</span>;
  }

  const visibleFiles = files.slice(0, MAX_VISIBLE_FILES);
  const hiddenFilesCount = files.length - visibleFiles.length;
  const isExpandable = hiddenFilesCount > 0;

  const handleExpandClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handlePopoverDownload = (file: CargoContentFile) => {
    onDownload(file);
    handleClose();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', gap: '4px', alignItems: 'center', overflow: 'hidden' }}>
      {visibleFiles.map((file, index) => (
        <React.Fragment key={file.id}>
          {index > 0 && <span style={{ color: '#999', fontSize: '12px', flexShrink: 0 }}>|</span>}
          <span
            onClick={() => onDownload(file)}
            title={`${file.description}\n${downloadTitle}`}
            style={{
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: FILE_NAME_MAX_WIDTH,
              flexShrink: 0,
            }}
          >
            {file.description}
          </span>
        </React.Fragment>
      ))}

      {isExpandable && (
        <span
          onClick={handleExpandClick}
          title={expandTitle}
          aria-label={expandTitle}
          style={{
            cursor: 'pointer',
            fontSize: '12px',
            color: '#0d6efd',
            fontWeight: 600,
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '2px',
          }}
        >
          <i className="bi bi-three-dots" />
          {`+${hiddenFilesCount}`}
        </span>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1, maxHeight: 250, overflowY: 'auto', minWidth: 220 }}>
          <List dense>
            {files.map((file) => (
              <ListItemButton key={file.id} onClick={() => handlePopoverDownload(file)}>
                <ListItemText
                  primary={file.description}
                  primaryTypographyProps={{ style: { textDecoration: 'underline', fontSize: '13px' } }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Popover>
    </div>
  );
};

export default CargoContentList;
